import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, Trash2 } from 'lucide-react';
import {
  deleteSavedTable,
  getSavedTables,
  type SavedInvestmentTable,
} from '@/lib/investmentTableLibrary';

/** Aanvullende tabellen en de bibliotheek voor het investeringsblok. */
export interface InvestmentElementBarProps {
  onAddOverview: () => void;
  onAddRateCard: () => void;
  onPickSaved: (saved: SavedInvestmentTable) => void;
  /** Uit zodra het blok al een tarieventabel heeft; er kan er maar één zijn. */
  canAddRateCard: boolean;
}

export default function InvestmentElementBar({
  onAddOverview,
  onAddRateCard,
  onPickSaved,
  canAddRateCard,
}: InvestmentElementBarProps) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'root' | 'library'>('root');
  const [savedTables, setSavedTables] = useState<SavedInvestmentTable[]>([]);
  const anchorRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    setView('root');
  }, []);

  useEffect(() => {
    if (view === 'library') setSavedTables(getSavedTables());
  }, [view]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (anchorRef.current && !anchorRef.current.contains(event.target as Node)) close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  const handleDeleteSaved = useCallback((id: string) => {
    deleteSavedTable(id);
    setSavedTables(getSavedTables());
  }, []);

  return (
    <div contentEditable={false} data-html2canvas-ignore="true" className="investment-element-bar">
      <div className="investment-element-anchor" ref={anchorRef}>
        <button
          type="button"
          className="investment-element-trigger"
          data-active={open ? 'true' : undefined}
          aria-expanded={open}
          aria-haspopup="menu"
          onClick={() => (open ? close() : setOpen(true))}
        >
          Meer toevoegen
          <ChevronDown className="h-3 w-3 shrink-0 opacity-60" aria-hidden />
        </button>

        {open && (
          <div className="investment-element-menu" role="menu">
            {view === 'root' ? (
              <>
                <button
                  type="button"
                  role="menuitem"
                  className="investment-element-item"
                  onClick={() => {
                    onAddOverview();
                    close();
                  }}
                >
                  <span className="investment-element-item-name">Investeringsoverzicht</span>
                  <span className="investment-element-item-meta">
                    Een nieuwe tabel met totaal
                  </span>
                </button>

                {canAddRateCard && (
                  <button
                    type="button"
                    role="menuitem"
                    className="investment-element-item"
                    onClick={() => {
                      onAddRateCard();
                      close();
                    }}
                  >
                    <span className="investment-element-item-name">Tarieventabel</span>
                    <span className="investment-element-item-meta">
                      Losse tarieven zonder totaal
                    </span>
                  </button>
                )}

                <button
                  type="button"
                  role="menuitem"
                  className="investment-element-item"
                  onClick={() => setView('library')}
                >
                  <span className="investment-element-item-name">Uit bibliotheek</span>
                  <span className="investment-element-item-meta">
                    Een tabel die je eerder bewaarde
                  </span>
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="investment-element-back"
                  onClick={() => setView('root')}
                >
                  <ChevronLeft className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  Bibliotheek
                </button>

                {savedTables.length === 0 ? (
                  <p className="investment-element-empty">
                    Nog geen tabellen bewaard. Gebruik “Bewaren in bibliotheek” in het zijpaneel om
                    een tabel te bewaren.
                  </p>
                ) : (
                  savedTables.map((saved) => (
                    <div key={saved.id} className="investment-element-saved">
                      <button
                        type="button"
                        className="investment-element-item is-saved"
                        onClick={() => {
                          onPickSaved(saved);
                          close();
                        }}
                      >
                        <span className="investment-element-item-name">{saved.name}</span>
                        <span className="investment-element-item-meta">
                          {saved.table.pricingType} · {saved.table.rows?.length ?? 0} regels
                        </span>
                      </button>
                      <button
                        type="button"
                        title="Uit bibliotheek verwijderen"
                        className="investment-element-saved-remove"
                        onClick={() => handleDeleteSaved(saved.id)}
                      >
                        <Trash2 className="h-3 w-3" aria-hidden />
                      </button>
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
